import { Capacitor, registerPlugin } from '@capacitor/core';
import api from '../services/api';

/**
 * The native side of a call (design_handoff_messages Part B §7), one shape over two plugins:
 * `CallKit` on iOS (CallKit + PushKit + AVAudioSession, ios/App/App/CallKitPlugin.swift) and
 * `PortalCall` on Android (the incoming / ongoing notifications, the foreground service and
 * the speakerphone, android/.../CallPlugin.java). On the web every call here is a no-op.
 *
 * CallProvider owns the call; this file only reports to the OS and forwards what the OS did.
 */
const CallKit = registerPlugin('CallKit');
const PortalCall = registerPlugin('PortalCall');

export const nativePlatform = () => (Capacitor.isNativePlatform?.() ? Capacitor.getPlatform() : null);
const ios = () => nativePlatform() === 'ios';
const android = () => nativePlatform() === 'android';
const quiet = (p) => Promise.resolve(p).catch(() => null);

export const getAndroidCallSettings = () => PortalCall.getCallSettings();
export const openAndroidCallSettings = (section) => PortalCall.openCallSettings({ section });

/** A push / notification payload (all strings) → the invite shape the provider uses. */
export function normalizeInvite(raw) {
  if (!raw || !raw.callId) return null;
  const peers = typeof raw.peers === 'string' ? raw.peers.split(',').filter(Boolean) : (raw.peers || []);
  return {
    callId: raw.callId,
    threadId: raw.threadId,
    video: raw.video === true || raw.video === 'true',
    from: raw.from || { userId: raw.fromUserId, displayName: raw.fromName || '', avatarUrl: raw.fromAvatarUrl || null },
    peers,
    sentAt: raw.sentAt || null,
  };
}

/**
 * iOS: the PushKit token, registered as platform `ios-voip` so the server can ring this phone
 * through CallKit. Called once the person is signed in; the token can arrive before or after.
 */
export function registerVoipToken() {
  if (!ios()) return () => {};
  const send = async (token) => {
    if (!token) return;
    try {
      await api.post('/push/native-subscribe', { token, platform: 'ios-voip', appVersion: process.env.REACT_APP_VERSION || null, deviceName: navigator.userAgent.slice(0, 200) });
    } catch { /* retried on the next launch */ }
  };
  quiet(CallKit.getVoipToken()).then((r) => send(r?.token));
  let handle = null;
  quiet(CallKit.addListener('voipToken', (e) => send(e?.token))).then((h) => { handle = h; });
  return () => { handle?.remove?.(); };
}

/**
 * What the OS did with a call before the JS asked: an invite drawn from a push, whether the
 * person already answered it on the system UI, a decline taken from the shade.
 *   → { invite, answered, declinedCallId, ended }
 */
export async function getPendingNative() {
  if (ios()) {
    const r = await quiet(CallKit.getPending());
    return { invite: normalizeInvite(r?.invite), answered: !!r?.answered, ended: !!r?.ended, declinedCallId: null };
  }
  if (android()) {
    const r = await quiet(PortalCall.getPending());
    return { invite: normalizeInvite(r?.invite), answered: !!r?.answered, ended: false, declinedCallId: r?.declinedCallId || null };
  }
  return { invite: null, answered: false, ended: false, declinedCallId: null };
}

/**
 * Listen to the OS: `incoming(invite)`, `answer(callId)`, `end(callId, reason)`, `mute(muted)`,
 * `speaker()`, `remoteEnd(callId)`, `audioActive(active)`, `open()`. Returns a release function.
 */
export function onNativeCall(handlers) {
  const handles = [];
  let disposed = false;
  const add = (plugin, event, fn) => quiet(plugin.addListener(event, (e) => {
    if (!disposed) fn(e);
  })).then((h) => {
    if (!h) return;
    if (disposed) h.remove?.(); else handles.push(h);
  });
  if (ios()) {
    // Only subscribe to events this consumer handles. CallKit checks hasListeners
    // before deciding whether to retain a lock-screen answer for the provider.
    if (handlers.incoming) add(CallKit, 'incoming', (e) => handlers.incoming(normalizeInvite(e)));
    if (handlers.answer) add(CallKit, 'answer', (e) => handlers.answer(e?.callId));
    if (handlers.end) add(CallKit, 'end', (e) => handlers.end(e?.callId, e?.reason));
    if (handlers.mute) add(CallKit, 'mute', (e) => handlers.mute(!!e?.muted));
    if (handlers.remoteEnd) add(CallKit, 'remoteEnd', (e) => handlers.remoteEnd(e?.callId));
    if (handlers.audioActive) add(CallKit, 'audioActive', (e) => handlers.audioActive(!!e?.active));
  } else if (android()) {
    add(PortalCall, 'action', (e) => {
      switch (e?.action) {
        case 'incoming': handlers.incoming?.(normalizeInvite(e)); break;
        case 'accept': handlers.incoming?.(normalizeInvite(e), { answered: true }); handlers.answer?.(e?.callId); break;
        case 'decline': handlers.end?.(e?.callId, 'user'); break;
        case 'mute': handlers.mute?.(null); break;
        case 'speaker': handlers.speaker?.(); break;
        case 'end': handlers.end?.(e?.callId, 'user'); break;
        case 'open': handlers.open?.(); break;
        default: break;
      }
    });
    add(PortalCall, 'remoteEnd', (e) => handlers.remoteEnd?.(e?.callId));
  }
  return () => { disposed = true; handles.forEach((h) => h.remove?.()); };
}

/** Reports to the OS. Every call is a no-op on the web. */
export const nativeCall = {
  // Never swallow denial: dialing/answering must wait for actual OS consent.
  ensureMediaPermissions: ({ video }) => {
    if (ios()) return CallKit.ensureMediaPermissions({ video: !!video });
    if (android()) return PortalCall.ensureMediaPermissions({ video: !!video });
    return Promise.resolve();
  },
  /** Dialing: the system shows the call as ours from the first second (iOS). */
  startOutgoing: ({ callId, name, video }) => {
    if (ios()) return quiet(CallKit.startOutgoing({ callId, name, video: !!video }));
    if (android()) return quiet(PortalCall.prepareAudio({ speaker: !!video }));
    return Promise.resolve();
  },
  /** An invite that came over the socket: CallKit learns of it (iOS); Android rings the notification when the app is in the background. */
  reportIncoming: (inv) => {
    if (ios()) return quiet(CallKit.reportIncoming({ callId: inv.callId, name: inv.from?.displayName || 'Portal', video: !!inv.video }));
    if (android()) {
      quiet(PortalCall.setHandling({ callId: inv.callId }));
      return quiet(PortalCall.showIncoming({ callId: inv.callId, threadId: inv.threadId, video: inv.video ? 'true' : 'false', fromUserId: inv.from?.userId, fromName: inv.from?.displayName || '', fromAvatarUrl: inv.from?.avatarUrl || '', peers: (inv.peers || []).join(','), sentAt: inv.sentAt || '' }));
    }
    return Promise.resolve();
  },
  /** The call went live. */
  connected: ({ callId, name, video, muted, speaker }) => {
    if (ios()) return quiet(CallKit.reportConnected({ callId }));
    if (android()) return quiet(PortalCall.startOngoing({ callId, name, video: !!video, muted: !!muted, speaker: !!speaker, timer: '0:00' }));
    return Promise.resolve();
  },
  /** The ongoing notification's words (Android): timer and toggles. */
  update: ({ callId, name, video, muted, speaker, timer }) => (android() ? quiet(PortalCall.updateOngoing({ callId, name, video: !!video, muted: !!muted, speaker: !!speaker, timer })) : Promise.resolve()),
  /** Our side ended it, or learned it ended: `reason` ended · cancelled · noanswer · declined · remote · failed. */
  ended: ({ callId, reason }) => {
    if (ios()) return quiet(CallKit.endCall({ callId, reason }));
    if (android()) return quiet(PortalCall.stopOngoing({ callId }));
    return Promise.resolve();
  },
  /** An incoming call we answered / declined in the app: the ringing notification goes (Android). */
  incomingHandled: () => (android() ? quiet(PortalCall.cancelIncoming()) : Promise.resolve()),
  answerIncoming: ({ video = false } = {}) => {
    if (ios()) return quiet(CallKit.answerIncoming());
    if (android()) return quiet(PortalCall.prepareAudio({ speaker: !!video }));
    return Promise.resolve();
  },
  setMuted: ({ callId, muted }) => (ios() ? quiet(CallKit.setMuted({ callId, muted: !!muted })) : Promise.resolve()),
  /** SPEAKER: the loudspeaker on or off (both platforms route the audio for real). */
  setSpeaker: (on) => {
    if (ios()) return quiet(CallKit.setSpeaker({ on: !!on }));
    if (android()) return quiet(PortalCall.setSpeaker({ on: !!on }));
    return Promise.resolve();
  },
};
